import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, TOKEN_KEY } from "@/lib/api";
import { Section } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { Trash2, Mail, Phone, Sparkles, LogOut } from "lucide-react";
import { blogSeed } from "@/config/site";

const categories = ["Strony internetowe", "Branding", "Social media", "Marketing", "AI w biznesie", "Rozwój firmy"];

export default function Admin() {
    const navigate = useNavigate();
    const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const [submissions, setSubmissions] = useState([]);
    const [posts, setPosts] = useState([]);
    const [genTitle, setGenTitle] = useState("");
    const [genCategory, setGenCategory] = useState(categories[0]);
    const [generating, setGenerating] = useState(false);

    const load = async () => {
        try {
            const [subs, pp] = await Promise.all([
                api.get("/admin/submissions"),
                api.get("/admin/blog/posts"),
            ]);
            setSubmissions(subs.data);
            setPosts(pp.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setAuthed(false);
            }
        }
    };

    useEffect(() => { if (authed) load(); }, [authed]);

    const onLogin = async (ev) => {
        ev.preventDefault();
        setLoading(true);
        try {
            const r = await api.post("/auth/login", { email, password });
            localStorage.setItem(TOKEN_KEY, r.data.token);
            setAuthed(true);
            toast.success("Zalogowano");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Nieprawidłowe dane");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        navigate("/");
    };

    const deleteSub = async (id) => {
        if (!window.confirm("Usunąć zapytanie?")) return;
        await api.delete(`/admin/submissions/${id}`);
        toast.success("Usunięto");
        load();
    };

    const updateStatus = async (id, status) => {
        await api.patch(`/admin/submissions/${id}`, { status });
        load();
    };

    const generatePost = async () => {
        if (!genTitle.trim()) { toast.error("Podaj tytuł"); return; }
        setGenerating(true);
        try {
            await api.post("/admin/blog/generate", { title: genTitle, category: genCategory });
            toast.success("Artykuł wygenerowany");
            setGenTitle("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Błąd generowania");
        } finally {
            setGenerating(false);
        }
    };

    const deletePost = async (slug) => {
        if (!window.confirm("Usunąć artykuł?")) return;
        await api.delete(`/admin/blog/posts/${slug}`);
        toast.success("Usunięto");
        load();
    };

    if (!authed) {
        return (
            <>
                <SEO title="Panel administracyjny – Szymkas Agency" description="Logowanie" />
                <Section variant="gray" className="min-h-screen pt-32">
                    <div className="max-w-md mx-auto rounded-[20px] bg-white border border-[#E5E7EB] p-7 md:p-8 shadow-sm">
                        <h1 className="font-display font-bold text-2xl mb-1 text-[#0B1220]">Panel administracyjny</h1>
                        <p className="text-sm text-[#0B1220]/60 mb-6">Zaloguj się aby zarządzać treścią.</p>
                        <form onSubmit={onLogin} data-testid="admin-login-form" className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold mb-1.5 text-[#0B1220]">E-mail</label>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email" className="w-full rounded-[12px] border border-[#E5E7EB] px-4 py-3 text-[15px] focus:outline-none focus:border-[#14B8A6] focus:ring-2 focus:ring-[#14B8A6]/20" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1.5 text-[#0B1220]">Hasło</label>
                                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password" className="w-full rounded-[12px] border border-[#E5E7EB] px-4 py-3 text-[15px] focus:outline-none focus:border-[#14B8A6] focus:ring-2 focus:ring-[#14B8A6]/20" />
                            </div>
                            <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full px-5 py-3 rounded-[12px] bg-[#14B8A6] hover:bg-[#0EA5A5] text-white font-semibold disabled:opacity-60 transition-all">
                                {loading ? "Logowanie…" : "Zaloguj się"}
                            </button>
                        </form>
                    </div>
                </Section>
            </>
        );
    }

    return (
        <>
            <SEO title="Panel administracyjny – Szymkas Agency" description="Zarządzanie treścią" />
            <Section variant="gray" className="pt-32 min-h-screen">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B1220]">Panel administracyjny</h1>
                        <p className="text-sm text-[#0B1220]/60 mt-1">Zarządzaj zapytaniami i treściami bloga.</p>
                    </div>
                    <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B1220]/70 hover:text-[#14B8A6]">
                        <LogOut className="w-4 h-4" /> Wyloguj
                    </button>
                </div>

                {/* Submissions */}
                <div className="rounded-[20px] bg-white border border-[#E5E7EB] p-6 md:p-8 mb-10">
                    <h2 className="font-display font-bold text-xl md:text-2xl mb-1 text-[#0B1220]">Zapytania ({submissions.length})</h2>
                    <p className="text-sm text-[#0B1220]/60 mb-5">Wiadomości z formularzy kontaktowych.</p>
                    {submissions.length === 0 ? (
                        <p className="text-sm text-[#0B1220]/50">Brak zapytań.</p>
                    ) : (
                        <div className="space-y-4">
                            {submissions.map((s) => (
                                <div key={s.id} className="rounded-[16px] border border-[#E5E7EB] p-5" data-testid={`admin-sub-${s.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                        <div>
                                            <div className="font-display font-semibold text-lg text-[#0B1220]">{s.name} {s.company && <span className="text-[#0B1220]/50 font-normal text-sm">— {s.company}</span>}</div>
                                            <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#0B1220]/60">
                                                <a href={`mailto:${s.email}`} className="hover:text-[#14B8A6] flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</a>
                                                {s.phone && <a href={`tel:${s.phone}`} className="hover:text-[#14B8A6] flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</a>}
                                                <span>{new Date(s.created_at).toLocaleString("pl-PL")}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select value={s.status} onChange={(e) => updateStatus(s.id, e.target.value)} className="text-xs rounded-md border border-[#E5E7EB] px-2 py-1" data-testid={`admin-sub-status-${s.id}`}>
                                                <option value="new">Nowe</option>
                                                <option value="contacted">W kontakcie</option>
                                                <option value="closed">Zamknięte</option>
                                            </select>
                                            <button onClick={() => deleteSub(s.id)} className="text-red-600 hover:bg-red-50 rounded-md p-1.5" data-testid={`admin-sub-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </div>
                                    {s.package && <div className="text-xs mb-2"><span className="font-semibold">Pakiet:</span> {s.package}</div>}
                                    {s.services?.length > 0 && <div className="text-xs mb-2"><span className="font-semibold">Usługi:</span> {s.services.join(", ")}</div>}
                                    {(s.budget || s.timeline) && (
                                        <div className="text-xs mb-2 flex gap-4">
                                            {s.budget && <span><span className="font-semibold">Budżet:</span> {s.budget}</span>}
                                            {s.timeline && <span><span className="font-semibold">Termin:</span> {s.timeline}</span>}
                                        </div>
                                    )}
                                    <p className="text-sm text-[#0B1220]/80 mt-2 whitespace-pre-wrap">{s.message}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Blog generation */}
                <div className="rounded-[20px] bg-white border border-[#E5E7EB] p-6 md:p-8">
                    <h2 className="font-display font-bold text-xl md:text-2xl mb-1 text-[#0B1220]">Blog</h2>
                    <p className="text-sm text-[#0B1220]/60 mb-5">Generuj nowe artykuły AI lub usuwaj istniejące.</p>

                    <div className="rounded-[12px] bg-[#F6F8FA] border border-[#E5E7EB] p-5 mb-6">
                        <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-[#14B8A6]" /><span className="font-semibold text-[#0B1220]">Generuj artykuł AI</span></div>
                        <div className="grid sm:grid-cols-2 gap-3 mb-3">
                            <input type="text" placeholder="Tytuł artykułu" value={genTitle} onChange={(e) => setGenTitle(e.target.value)} data-testid="admin-blog-title" className="rounded-[12px] border border-[#E5E7EB] px-4 py-2.5 text-sm" />
                            <select value={genCategory} onChange={(e) => setGenCategory(e.target.value)} data-testid="admin-blog-category" className="rounded-[12px] border border-[#E5E7EB] px-4 py-2.5 text-sm">
                                {categories.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {blogSeed.map((b) => (
                                <button key={b.slug} onClick={() => { setGenTitle(b.title); setGenCategory(b.category); }} className="text-xs px-3 py-1 rounded-full border border-[#E5E7EB] hover:border-[#14B8A6] hover:text-[#14B8A6] transition-colors" data-testid={`admin-blog-seed-${b.slug}`}>
                                    {b.title}
                                </button>
                            ))}
                        </div>
                        <button onClick={generatePost} disabled={generating} data-testid="admin-blog-generate" className="px-5 py-2.5 rounded-[12px] bg-[#14B8A6] hover:bg-[#0EA5A5] text-white text-sm font-semibold disabled:opacity-60">
                            {generating ? "Generowanie… (może potrwać 20-40s)" : "Wygeneruj artykuł"}
                        </button>
                    </div>

                    <h3 className="font-semibold text-sm mb-3 text-[#0B1220]">Opublikowane artykuły ({posts.length})</h3>
                    {posts.length === 0 ? (
                        <p className="text-sm text-[#0B1220]/50">Brak artykułów.</p>
                    ) : (
                        <div className="space-y-2">
                            {posts.map((p) => (
                                <div key={p.slug} className="flex items-center justify-between rounded-[12px] border border-[#E5E7EB] px-4 py-3" data-testid={`admin-post-${p.slug}`}>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-[#0B1220] truncate">{p.title}</div>
                                        <div className="text-xs text-[#0B1220]/60">{p.category} • {new Date(p.created_at).toLocaleDateString("pl-PL")}</div>
                                    </div>
                                    <button onClick={() => deletePost(p.slug)} className="text-red-600 hover:bg-red-50 rounded-md p-1.5" data-testid={`admin-post-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Section>
        </>
    );
}
